import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { formatUKDate } from "@/lib/date";
import { Banknote, Trophy, Gift } from "lucide-react";

function formatPounds(pence) {
  if (!pence) return "£0";
  // Drop trailing .00 for whole-pound values so £50 reads clean; keeps
  // pence (e.g. £4.50) for anything with a fractional part. Matches the
  // login chip's convention (iter91 UX polish).
  return `£${(pence / 100).toFixed(2).replace(/\.00$/, "")}`;
}

function describeSplit(pool) {
  if (!pool) return null;
  switch (pool.payout_mode) {
    case "winner_all":
      return "Winner takes all (100%)";
    case "50_30_20":
      return "1st 50% · 2nd 30% · 3rd 20%";
    case "custom": {
      const s = pool.payout_split || {};
      return `Custom — 1st ${s.first ?? 0}% · 2nd ${s.second ?? 0}% · 3rd ${s.third ?? 0}%`;
    }
    default:
      return null;
  }
}

/**
 * Visible above the matches and below the scoring hint when:
 *  - the editor has set a current match-day prize fund, OR
 *  - the editor has set an end-of-league season prize, OR
 *  - paid entry is enabled.
 *
 * The end-of-season prize is rendered as a hero strip — it's the strongest
 * retention hook for players, so it dominates the banner.
 */
export default function ProLeagueBanner({ matchDate }) {
  const { user } = useAuth();
  // iter87 — Slug-is-Master. Prize Pool banner values come from
  // `brand.prize_fund_pence` / `brand.prize_pool_text` (fetched by URL slug
  // in BrandContext), NOT the caller's `user.pub_id` (which is null for
  // client-role brewery admins). No toggle — presence of a prize is what
  // gates the banner: if the pub has ANY prize configured (fund > 0 OR
  // text set) we show it; otherwise the banner hides.
  const { brand } = useBrand();
  const [pool, setPool] = useState(null);
  const [myRank, setMyRank] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!matchDate) return undefined;
    (async () => {
      try {
        const { data } = await api.get(`/pools/${matchDate}`);
        if (!cancelled) setPool(data);
      } catch {
        if (!cancelled) setPool(null);
      }
    })();
    return () => { cancelled = true; };
  }, [matchDate]);

  // Fetch overall leaderboard to find current user's position (no fetch if no user).
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return undefined;
    (async () => {
      try {
        const { data } = await api.get("/leaderboard");
        if (cancelled) return;
        const idx = data.findIndex((r) => r.user_id === user.id);
        setMyRank(idx >= 0 ? { rank: idx + 1, total: data.length } : null);
      } catch {
        if (!cancelled) setMyRank(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  if (!pool) return null;
  // iter91 — Prize values now come EXCLUSIVELY from the URL-slug brand
  // (populated by the mirror-on-write in `PUT /pool-config` +
  // `PUT /season-prize-config` when the Editor is scoped via `?as_pub=`).
  // The pub-scoped `GET /pools/{match_date}` zeroes out all global monetary
  // fields, so we never read `pool.current_prize_fund_pence` etc for a
  // pub view — that would just be zero. Only `pool.pub_prize_*` (deprecated
  // fallback) and the pool's own `stripe_enabled` are still read from the
  // pool doc.
  const brandMatchDayPence = Number(brand?.current_match_day_prize_pence || 0);
  const brandSeasonPence = Number(brand?.prize_fund_pence || 0);
  const brandVoucherCount = Number(brand?.voucher_count || 0);
  const brandVoucherText = (brand?.prize_pool_text || "").trim();
  const brandVoucherExpiry = (brand?.voucher_expires_at || "").trim();
  // iter147 — % of entry fees mode. When the pub uses "percent", the
  // effective season pot is the projected value computed server-side
  // from paid league_entry transactions × percent.
  const seasonPrizeType = (brand?.season_prize_type || "fixed").trim();
  const seasonPrizePercent = Number(brand?.season_prize_percent || 0);
  const projectedSeasonPence = Number(brand?.projected_season_prize_pence || 0);
  // Fallback for legacy pub-scoped views (before iter91 fields were on
  // the pub doc) — keeps existing pubs' banners rendering.
  const seasonPence =
    seasonPrizeType === "percent"
      ? projectedSeasonPence
      : (brandSeasonPence || Number(pool.pub_prize_fund_pence || 0));
  const matchDayPence = brandMatchDayPence;
  const voucherText = brandVoucherText || (pool.pub_prize_text || "").trim();
  const voucherCount = brandVoucherCount;
  const voucherExpiry = brandVoucherExpiry;
  const hasMatchDay = matchDayPence > 0;
  const hasSeason =
    seasonPrizeType === "percent" ? seasonPrizePercent > 0 : seasonPence > 0;
  const hasVoucher = !!voucherText && voucherCount > 0;
  const paid = !!pool.stripe_enabled;
  if (!paid && !hasMatchDay && !hasSeason && !hasVoucher) return null;

  const split = describeSplit(pool);

  return (
    <section data-testid="prize-pool-banner" className="mb-10 space-y-3">
      {/* iter91 — Unified per-pub prize hero. Shows up to four tiles:
          "Today", "Season", voucher line, and (paid entry only) an entry-fee
          strip. Every tile is opt-in based on the pub's saved fields. */}
      {(hasMatchDay || hasSeason || hasVoucher) && (
        <div
          data-testid="pub-prize-hero"
          className="relative overflow-hidden rounded-sm border border-emerald-300 bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50 p-4 sm:p-5"
        >
          <span className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-emerald-300/30 blur-3xl" aria-hidden="true" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
              <span className="inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-gradient-to-b from-emerald-500 to-emerald-700 text-white shadow shrink-0">
                <Trophy className="h-5 w-5 sm:h-6 sm:w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
                  {hasMatchDay && (
                    <div data-testid="pub-prize-matchday">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Match-day prize</p>
                      <p className="font-heading text-2xl font-bold tabular-nums text-emerald-900 leading-none mt-0.5" data-testid="pub-prize-matchday-amount">
                        {formatPounds(matchDayPence)}
                      </p>
                    </div>
                  )}
                  {hasSeason && (
                    <div data-testid="pub-prize-season">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Current league prize</p>
                      <p className="font-heading text-2xl font-bold tabular-nums text-emerald-900 leading-none mt-0.5" data-testid="pub-prize-season-amount">
                        {formatPounds(seasonPence)}
                      </p>
                      {seasonPrizeType === "percent" && (
                        <p
                          className="text-sm sm:text-base font-heading font-bold text-emerald-900 mt-1"
                          data-testid="pub-prize-season-percent"
                        >
                          ({seasonPrizePercent}% of entry fees)
                        </p>
                      )}
                    </div>
                  )}
                  {hasVoucher && (
                    <div data-testid="pub-prize-voucher">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 inline-flex items-center gap-1">
                        <Gift className="h-3 w-3" /> Vouchers
                      </p>
                      <p className="font-heading text-base sm:text-lg font-bold text-emerald-900 leading-tight mt-0.5">
                        {voucherCount > 0 ? `${voucherCount} × ` : ""}{voucherText}
                      </p>
                      {voucherExpiry && (
                        <p className="text-[10px] font-medium text-emerald-700 mt-0.5" data-testid="pub-prize-voucher-expiry">
                          expires {formatUKDate(voucherExpiry, { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {myRank?.rank && (
              <div
                data-testid="my-rank-chip"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-white/70 border border-emerald-300 shrink-0"
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">You&apos;re currently</span>
                <span className="font-heading text-xl font-bold tabular-nums text-emerald-900">#{myRank.rank}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SECONDARY — entry fee / prize-split (paid entry only). Match-day
          fund and season fund now live on the pub-prize hero above; this
          strip is only relevant when Stripe is on. */}
      {paid && (
        <div className="p-5 rounded-sm border border-[#0038FF]/30 bg-[#0038FF]/[0.04]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div data-testid="banner-fee">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
                <Banknote className="h-3.5 w-3.5" /> Entry fee
              </p>
              <p className="font-heading text-2xl font-bold tabular-nums text-zinc-950 mt-0.5" data-testid="pool-fee">
                {formatPounds(pool.entry_fee_pence)}
              </p>
            </div>
            <div className="lg:col-span-2 min-w-0" data-testid="banner-split">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5" /> Prize split
              </p>
              <p className="text-sm text-zinc-950 font-semibold mt-0.5 leading-snug" data-testid="pool-split">{split}</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            Pay your entry fee to be entered for this match-day&apos;s prize. Free play continues for the Daily &amp; Overall leagues.
          </p>
        </div>
      )}
    </section>
  );
}
